// ═══════════════════════════════════════════════════════════════
// ATTACHMENT LIST — File attachments for member notes
// Path: src/components/donorDetail/AttachmentList.jsx
//
// Renders below the Notes textarea. Supports upload, download,
// and delete. Files go to Supabase Storage; metadata stored
// in members.attachments JSONB.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useRef } from "react";
import {
  Paperclip, Upload, Trash2, Download, File, Image, FileText,
  FileSpreadsheet, Loader,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { attachmentService } from "../../services/attachmentService";

// ── File type icon helper ────────────────────────────────────
const getFileIcon = (type) => {
  if (!type) return File;
  if (type.startsWith("image/")) return Image;
  if (type === "application/pdf") return FileText;
  if (type.includes("spreadsheet") || type.includes("csv") || type.includes("excel"))
    return FileSpreadsheet;
  return File;
};

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export const AttachmentList = ({
  memberId,
  attachments = [],
  canEdit,
  palette,
  onAttachmentsChange,
  showMessage,
}) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const currentAttachments = [...(attachments || [])];
    let uploadCount = 0;

    for (const file of files) {
      try {
        const attachment = await attachmentService.upload(memberId, file);
        currentAttachments.push(attachment);
        uploadCount++;
      } catch (err) {
        showMessage?.(`Failed to upload "${file.name}": ${err.message}`, "error");
      }
    }

    if (uploadCount > 0) {
      try {
        await attachmentService.saveAttachments(memberId, currentAttachments);
        onAttachmentsChange?.(currentAttachments);
        showMessage?.(`${uploadCount} file${uploadCount !== 1 ? "s" : ""} attached`);
      } catch (err) {
        showMessage?.("Files uploaded but failed to save metadata: " + err.message, "error");
      }
    }

    setUploading(false);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (attachment, index) => {
    setDeleting(index);
    try {
      await attachmentService.remove(attachment.path);
      const updated = attachments.filter((_, i) => i !== index);
      await attachmentService.saveAttachments(memberId, updated);
      onAttachmentsChange?.(updated);
      showMessage?.(`"${attachment.name}" removed`);
    } catch (err) {
      showMessage?.("Failed to remove attachment: " + err.message, "error");
    } finally {
      setDeleting(null);
    }
  };

  const hasAttachments = attachments && attachments.length > 0;

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Paperclip size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
          <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text3, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Attachments{hasAttachments ? ` (${attachments.length})` : ""}
          </span>
        </div>
        {canEdit && (
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "4px 12px", borderRadius: 6,
                fontSize: FONT_SIZES.xs, fontWeight: 600,
                fontFamily: FONT_FAMILY, cursor: uploading ? "wait" : "pointer",
                border: `1px solid ${palette.accent1}40`,
                background: `${palette.accent1}08`,
                color: palette.accent1,
              }}
            >
              {uploading ? <Loader size={ICON_SIZES.xs} style={{ animation: "spin 1s linear infinite" }} /> : <Upload size={ICON_SIZES.xs} />}
              {uploading ? "Uploading…" : "Attach File"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleUpload}
              style={{ display: "none" }}
            />
          </>
        )}
      </div>

      {/* File list */}
      {hasAttachments && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {attachments.map((att, index) => {
            const IconComp = getFileIcon(att.type);
            const isDeleting = deleting === index;
            return (
              <div
                key={att.path || index}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 12px", borderRadius: 8,
                  border: `1px solid ${palette.cardBorder}`,
                  background: palette.inputBg,
                  transition: "background 0.12s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${palette.accent1}06`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = palette.inputBg; }}
              >
                <IconComp size={ICON_SIZES.sm} style={{ color: palette.accent1, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: FONT_SIZES.sm, fontWeight: 600, color: palette.text1,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {att.name}
                  </div>
                  <div style={{ fontSize: 10, color: palette.text3 }}>
                    {attachmentService.formatFileSize(att.size)}
                    {att.uploadedAt && (
                      <> &middot; {new Date(att.uploadedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</>
                    )}
                  </div>
                </div>
                <a
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Download"
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 28, height: 28, borderRadius: 6,
                    border: `1px solid ${palette.cardBorder}`,
                    background: "transparent", color: palette.accent1,
                    cursor: "pointer", flexShrink: 0, textDecoration: "none",
                  }}
                >
                  <Download size={ICON_SIZES.sm} />
                </a>
                {canEdit && (
                  <button
                    onClick={() => handleDelete(att, index)}
                    disabled={isDeleting}
                    title="Remove attachment"
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      width: 28, height: 28, borderRadius: 6,
                      border: "none", background: "#fee2e2",
                      color: "#dc2626", cursor: isDeleting ? "wait" : "pointer",
                      flexShrink: 0, opacity: isDeleting ? 0.5 : 1,
                    }}
                  >
                    {isDeleting ? <Loader size={ICON_SIZES.xs} style={{ animation: "spin 1s linear infinite" }} /> : <Trash2 size={ICON_SIZES.xs} />}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state (only when editable) */}
      {!hasAttachments && canEdit && (
        <div
          onClick={() => fileInputRef.current?.click()}
          style={{
            padding: "14px 16px", borderRadius: 8,
            border: `1px dashed ${palette.inputBorder}`,
            background: `${palette.inputBg}60`,
            textAlign: "center", cursor: "pointer",
            fontSize: FONT_SIZES.xs, color: palette.text3,
            transition: "border-color 0.15s, background 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = palette.accent1;
            e.currentTarget.style.background = `${palette.accent1}06`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = palette.inputBorder;
            e.currentTarget.style.background = `${palette.inputBg}60`;
          }}
        >
          <Upload size={ICON_SIZES.sm} style={{ color: palette.text3, marginBottom: 4, display: "block", margin: "0 auto 4px" }} />
          Click to attach files (PDF, images, documents — 10 MB max)
        </div>
      )}
    </div>
  );
};
